import { useEffect, useState, useContext, useMemo, useCallback } from 'react'
import { t } from '@lingui/core/macro'
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined'
import CircularProgress from '@mui/material/CircularProgress'
import Fab from '@mui/material/Fab'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import makeStyles from '@mui/styles/makeStyles'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import debounce from 'lodash/debounce'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { Paper, TableCellTooltip } from '@components'
import { submissionContext } from '@contexts'
import { colors } from '@global/constants'
import { useQueryString } from '@global/hooks'
import { useSsoEnabled } from '@global/hooks/queries/useAuthHintQuery'
import { getCellContent } from 'functions'
import * as SubmissionActions from '../submissions.actions'
import { SubmissionStatusCell } from './components'
import { Toolbar } from './toolbar'
import { CvsTable } from '../../components/table/cvs-table'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    height: '100%',
  },
  tableRow: {
    cursor: 'pointer',
    maxWidth: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  tableCell: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}))

// fetchSubmissions returns 100 records per request
const LIMIT_PER_PAGE = 100

export const Submissions = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const classes = useStyles()
  const { byId, fetchSubmissions, submissionSummaryById } = useContext(submissionContext)
  const [displaySubmissions, setDisplaySubmissions] = useState([])
  const [params, setParams] = useState({ order_by: 'created_at', order: 'desc', offset: '0' })
  const { setUrl } = useQueryString(params, val => {
    if (typeof val === 'string') {
      setParams({ ...params, term: val })
      return
    }

    setParams(val)
  })
  const [DATASETS_PAGE_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )
  const templates = useSelector(state => state.Submissions.templatesById, shallowEqual)
  const templatesCount = Object.values(templates).length

  const submissionsWithSummary = useMemo(
    () =>
      Object.values(byId).map(sub => ({
        ...byId[sub?.id],
        summary: submissionSummaryById[sub?.submission_id],
      })),
    [byId, submissionSummaryById],
  )

  const { isInitialLoading: isLoading } = useQuery(
    [
      'searchSubmissions',
      {
        ...params,
      },
    ],
    () => fetchSubmissions({ ...params }),
    { refetchOnWindowFocus: false },
  )

  useEffect(() => {
    setDisplaySubmissions(submissionsWithSummary)
  }, [submissionsWithSummary])

  useEffect(() => {
    if (templatesCount) return

    dispatch(SubmissionActions.getTemplates())
  }, [templatesCount])

  const StatusComp = useCallback(
    ({ row }) => <SubmissionStatusCell submission={row} submissionSummary={row.summary} />,
    [],
  )

  const columns = useMemo(
    () => [
      {
        label: DATASETS_PAGE_ENABLED ? t`Datasets` : t`Templates`,
        type: 'text',
        width: '28%',
        lookup: 'template_name',
      },
      { label: t`Submission Name`, type: 'text', width: '22%', lookup: 'submission_name' },
      { label: t`Rows`, type: 'number', width: '7%', lookup: 'rows', blockSort: true },
      { label: t`Created By`, type: 'text', width: '14%', lookup: 'created_by' },
      { label: t`Last Submitted`, type: 'date', width: '12%', lookup: 'created_at' },
      {
        label: t`Status`,
        type: 'component',
        lookup: 'status',
        component: StatusComp,
      },
    ],
    [StatusComp],
  )

  const updateSearch = useCallback(
    debounce(e => {
      const val = e.target.value
      const newParams = {
        ...params,
        offset: '0',
        term: val,
      }

      setUrl(newParams)
    }, 250),
    [params],
  )

  const searchOnTemplate = temp => {
    const updatedParams = {
      ...params,
      offset: 0,
      template_id: temp,
    }
    setUrl(updatedParams)
  }

  const handleSort = sortBy => {
    const updatedParams = {
      ...params,
      offset: 0,
    }
    if (sortBy === params.order_by) {
      updatedParams.order = params.order === 'asc' ? 'desc' : 'asc'
    } else {
      updatedParams.order_by = sortBy
    }
    setUrl(updatedParams)
  }

  const handlePaging = btnClicked => {
    const updatedParams = { ...params }
    const currentOffset = Number(params.offset) || 0

    if (btnClicked === 'previous') {
      updatedParams.offset = currentOffset - LIMIT_PER_PAGE
    } else {
      updatedParams.offset = currentOffset + LIMIT_PER_PAGE
    }
    setUrl(updatedParams)
  }

  const handleClickRow = row => {
    history.push(`/codes/details/${row.dataset_id}/data`)
  }

  const ssoEnabled = useSsoEnabled()

  const { features } = useSelector(state => state.Auth)
  // This feature flag will be removed when there is sufficient usage and data to show sources in prod.
  const DATA_SOURCE_ENABLED = Boolean(features?.['show-data-source'])
  const prevBtnDisabled = isLoading || !Number(params.offset)
  const nextBtnDisabled = isLoading || submissionsWithSummary.length < LIMIT_PER_PAGE

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
    >
      <Paper elevation={2} className={classes.paper}>
        <div style={{ height: '100%' }}>
          <Toolbar
            templateSearchProps={{
              selected: params.template_id || [],
              onSelected: item => searchOnTemplate(item),
            }}
            searchProps={{
              onChange: updateSearch,
              defaultValue: params.term,
            }}
            paginationProps={{
              prevBtnDisabled,
              nextBtnDisabled,
              handlePaging,
            }}
          />
          {!isLoading ? (
            <div style={{ height: 'calc(100% - 120px)' }}>
              <CvsTable
                columns={columns}
                rows={displaySubmissions}
                orderBy={params.order_by}
                sortOrder={params.order}
                keyField="dataset_id"
                noAdd={true}
                noPagination={true}
                style={{ maxWidth: '100%' }}
                customHandleSort={handleSort}
                headerRowPadding={50}
                emptyStateMessage={{
                  icon: (
                    <Fab
                      style={{
                        height: '65px',
                        width: '65px',
                        color: colors.gray.light,
                        cursor: 'auto',
                      }}
                      disabled
                    >
                      <FileCopyOutlinedIcon
                        style={{ fontSize: '34px', color: colors.blue.smokey }}
                      />
                    </Fab>
                  ),
                  title: t`No Submissions Found`,
                  description: t`You can search for any submission created by you or any member of a group to which you belong`,
                }}
              >
                {({ row }) => {
                  const approvalFlowEnabled = Boolean(
                    templates[row.template_id]?.settings?.require_approval,
                  )
                  return (
                    <TableRow
                      hover
                      onClick={() => handleClickRow(row)}
                      tabIndex={-1}
                      key={row.dataset_id}
                      className={classes.tableRow}
                    >
                      <TableCellTooltip title={row.template_name}>
                        <TableCell
                          component="td"
                          scope="row"
                          padding="normal"
                          className={classes.tableCell}
                          style={{ paddingLeft: 50 }}
                        >
                          {row.template_name}
                        </TableCell>
                      </TableCellTooltip>
                      <TableCellTooltip title={row.submission_name}>
                        <TableCell
                          component="td"
                          scope="row"
                          padding="normal"
                          className={classes.tableCell}
                        >
                          {/* check for sso flag is set on the link below to give 3rd party users a way to open a new tab without entering an sso login loop */}
                          <a
                            href={
                              !ssoEnabled
                                ? `/codes/details/${row.dataset_id}/data?login=system`
                                : `/codes/details/${row.dataset_id}/data`
                            }
                            style={{ textDecoration: 'underline' }}
                          >
                            {row.submission_name}
                          </a>
                        </TableCell>
                      </TableCellTooltip>
                      <TableCell
                        component="td"
                        scope="row"
                        padding="normal"
                        className={classes.tableCell}
                      >
                        {getCellContent('number', row.rows)}
                      </TableCell>
                      <TableCell
                        component="td"
                        scope="row"
                        padding="normal"
                        className={classes.tableCell}
                      >
                        {DATA_SOURCE_ENABLED ? (row.source ?? row.created_by) : row.created_by}
                      </TableCell>
                      <TableCell
                        component="td"
                        scope="row"
                        padding="normal"
                        className={classes.tableCell}
                      >
                        {getCellContent('date', row.started_at || row.created_at)}
                      </TableCell>
                      <TableCell
                        component="td"
                        scope="row"
                        padding="normal"
                        className={classes.tableCell}
                      >
                        <SubmissionStatusCell
                          submission={row}
                          submissionSummary={row.summary}
                          approvalFlowEnabled={approvalFlowEnabled}
                        />
                      </TableCell>
                    </TableRow>
                  )
                }}
              </CvsTable>
            </div>
          ) : (
            <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
              <CircularProgress color="primary" />
            </div>
          )}
        </div>
      </Paper>
    </motion.div>
  )
}
